/**
 * IOSPricingPlanSelector — real Apple StoreKit 2 purchase surface.
 *
 * Compliance guarantees (Apple Guideline 3.1.1 / 3.1.3(b)):
 *  - No Stripe price ids, no Stripe checkout, no external URLs, no Safari.
 *  - Prices and currency come exclusively from StoreKit (never hardcoded).
 *  - "Restore Purchases" and "Manage Subscription" are always reachable.
 *
 * Rendered only when IAP_APPLE_ENABLED === true AND the user is on native iOS
 * with the StoreKit plugin available. Otherwise callers fall back to
 * <NativePremiumMessage />.
 */
import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useAppleIap } from "@/hooks/useAppleIap";
import { IapDiagnosticsPanel } from "@/components/IapDiagnosticsPanel";
import { APPLE_PRODUCT_IDS, type ApplePlanKey } from "@/lib/iap/apple-products";
import {
  APPLE_STORE_COPY,
  DUPLICATE_STRIPE_ON_APPLE_COPY,
  getAppleUiCopy,
  type UiLang,
} from "@/lib/iap/apple-ui-states";

const PLAN_ORDER: ApplePlanKey[] = ["monthly", "quarterly", "annual"];

function planLabel(planKey: ApplePlanKey, copy: typeof APPLE_STORE_COPY["es"]) {
  if (planKey === "monthly") return copy.monthly;
  if (planKey === "quarterly") return copy.quarterly;
  return copy.annual;
}

function planCadence(planKey: ApplePlanKey, copy: typeof APPLE_STORE_COPY["es"]) {
  if (planKey === "monthly") return copy.perMonth;
  if (planKey === "quarterly") return copy.perQuarter;
  return copy.perYear;
}

export function IOSPricingPlanSelector() {
  const { language } = useLanguage();
  const lang: UiLang = language === "en" ? "en" : "es";
  const copy = APPLE_STORE_COPY[lang];

  const {
    hasActiveStripeSubscription,
    hasActiveAppleSubscription,
  } = useSubscriptionContext();

  const {
    storeKitReady,
    products,
    loadingProducts,
    purchasingProductId,
    restoring,
    uiState,
    errorDetail,
    probingStoreKit,
    purchase,
    restore,
    manageSubscription,
    reloadProducts,
    diagnostics,
  } = useAppleIap();

  const statusMessage = uiState ? getAppleUiCopy(uiState, lang) : null;

  // Internal diagnostic codes stay in logs only — never rendered to the user.
  useEffect(() => {
    if (errorDetail) console.info("🍎 [IAP] errorDetail", errorDetail);
  }, [errorDetail]);

  const secondaryActions = (
    <div className="space-y-2 pt-2">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => void restore()}
        disabled={restoring}
        data-testid="ios-restore-purchases"
      >
        {restoring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {copy.restore}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => void manageSubscription()}
        data-testid="ios-manage-subscription"
      >
        {copy.manage}
      </Button>
    </div>
  );

  // A user who already pays elsewhere keeps access; never offer a second charge.
  if (hasActiveStripeSubscription && !hasActiveAppleSubscription) {
    const dupe = DUPLICATE_STRIPE_ON_APPLE_COPY[lang];
    return (
      <Card
        className="p-6 space-y-3"
        role="status"
        aria-live="polite"
        data-testid="ios-duplicate-stripe-notice"
      >
        <h3 className="text-lg font-semibold text-foreground">{dupe.title}</h3>
        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
          <li>{dupe.alreadyActive}</li>
          <li>{dupe.worksOnIos}</li>
          <li>{dupe.managedByStripe}</li>
          <li>{dupe.noAutoCancel}</li>
        </ul>
        {secondaryActions}
      </Card>
    );
  }

  if (hasActiveAppleSubscription) {
    return (
      <Card className="p-6 space-y-3" data-testid="ios-active-subscription">
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-foreground">{copy.activeTitle}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{copy.cancelAnytime}</p>
        {secondaryActions}
      </Card>
    );
  }

  if (!storeKitReady) {
    return (
      <Card className="p-6 space-y-3" data-testid="ios-store-unavailable">
        <h3 className="text-lg font-semibold text-foreground">{copy.title}</h3>
        <p className="text-sm text-muted-foreground">
          {probingStoreKit
            ? getAppleUiCopy("loading_products", lang)
            : getAppleUiCopy("product_unavailable", lang)}
        </p>
        {!probingStoreKit && (
          <Button type="button" variant="outline" onClick={() => void reloadProducts()}>
            {copy.retry}
          </Button>
        )}
        <IapDiagnosticsPanel snapshot={diagnostics} />
        {secondaryActions}
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-4" data-testid="ios-pricing-storekit">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{copy.title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{copy.subtitle}</p>
      </div>

      {loadingProducts && (
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {getAppleUiCopy("loading_products", lang)}
        </div>
      )}

      {!loadingProducts && products.length === 0 && (
        <div className="space-y-3" role="status" aria-live="polite">
          <p className="text-sm text-muted-foreground">
            {getAppleUiCopy("product_unavailable", lang)}
          </p>
          <Button type="button" variant="outline" onClick={() => void reloadProducts()}>
            {copy.retry}
          </Button>
        </div>
      )}

      {products.length > 0 && (
        <ul className="space-y-3">
          {PLAN_ORDER.map((planKey) => {
            const productId = APPLE_PRODUCT_IDS[planKey];
            const product = products.find((p) => p.productId === productId);
            if (!product) return null;
            const busy = purchasingProductId === productId;

            return (
              <li
                key={productId}
                className="rounded-lg border border-border p-4 space-y-3"
                data-testid={`ios-plan-${planKey}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-foreground">
                    {planLabel(planKey, copy)}
                  </span>
                  <span className="text-right">
                    {/* Localized price straight from StoreKit */}
                    <span className="text-lg font-semibold text-foreground">
                      {product.displayPrice}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {planCadence(planKey, copy)}
                    </span>
                  </span>
                </div>

                {product.introductoryOffer && (
                  <p className="text-xs text-muted-foreground">
                    {product.introductoryOffer.displayPrice} · {product.introductoryOffer.period}
                  </p>
                )}

                <Button
                  type="button"
                  className="w-full"
                  onClick={() => void purchase(planKey)}
                  disabled={
                    Boolean(purchasingProductId) ||
                    hasActiveAppleSubscription ||
                    hasActiveStripeSubscription
                  }
                  data-testid={`ios-buy-${planKey}`}
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {copy.subscribe}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {statusMessage && (
        <p
          className="text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
          data-testid="ios-iap-status"
        >
          {statusMessage}
        </p>
      )}

      <IapDiagnosticsPanel snapshot={diagnostics} />

      {secondaryActions}

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {copy.legal} {copy.renewsAutomatically}
      </p>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        <Link to="/legal/terms" className="text-primary hover:underline">
          {lang === "en" ? "Terms of Service" : "Términos de Servicio"}
        </Link>
        {" · "}
        <Link to="/legal/privacy" className="text-primary hover:underline">
          {lang === "en" ? "Privacy Policy" : "Política de Privacidad"}
        </Link>
      </p>
    </Card>
  );
}

export default IOSPricingPlanSelector;
